/**
 * App — the router root.
 *
 * Two jobs and nothing else: restore a persisted session before any screen can
 * read from it, and lay out the three nesting levels every feature route sits
 * inside — guard, shell, screen.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hammer } from 'lucide-react';
import type { CurrentUserDto } from '@nova/shared';
import { api } from '@/lib/api';
import { applyLanguage } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import LoginPage from '@/features/auth/pages/LoginPage';
import { featureRoutes } from '@/routes';
import { Card, EmptyState, Spinner } from '@/components/ui';

export default function App() {
  const { t } = useTranslation('auth');
  const restoring = useSessionBootstrap();

  if (restoring) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-2 bg-slate-100">
        <Spinner size="lg" className="text-blue-600" label={t('session.restoring')} />
        <p className="label-micro">{t('session.restoring')}</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Every screen in the system, registered in one place. Features do
              not register their own routes — a route that exists in only one
              branch is a route nobody else can link to. */}
          {featureRoutes()}

          {/* Anything unmatched still lands somewhere navigable rather than on
              a white screen. */}
          <Route path="*" element={<ScreenUnderConstruction />} />
        </Route>
      </Route>
    </Routes>
  );
}

/**
 * The tokens are persisted, the user object may not be (a cleared cache, a
 * partially written store, a token pasted in by QA). One `/auth/me` rehydrates
 * permissions and language before any guard reads them — without it a reload
 * would briefly evaluate every permission as `false` and paint forbidden panels
 * across a perfectly valid session.
 */
function useSessionBootstrap(): boolean {
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const [restoring, setRestoring] = useState(() => Boolean(accessToken) && !user);
  const requested = useRef(false);

  useEffect(() => {
    // A new sign-in writes tokens and user together, so this only ever fires for
    // a genuinely half-restored session.
    if (!accessToken) {
      requested.current = false;
      setRestoring(false);
      return;
    }
    if (user || requested.current) return;

    requested.current = true;
    setRestoring(true);
    let cancelled = false;

    api
      .get<CurrentUserDto>('/auth/me')
      .then(({ data }) => {
        if (cancelled) return;
        useAuthStore.getState().setUser(data);
        applyLanguage(data.language); // NFR-03
      })
      .catch(() => {
        // Dropping the tokens is what sends the user to /login: ProtectedRoute
        // redirects on the next render and keeps the attempted path.
        if (!cancelled) useAuthStore.getState().clear();
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, user]);

  return restoring;
}

function ScreenUnderConstruction() {
  const { t } = useTranslation('auth');
  const location = useLocation();

  return (
    <div className="flex h-full items-start justify-center p-4">
      <Card className="w-full max-w-md">
        <EmptyState
          icon={Hammer}
          title={t('placeholder.title')}
          description={
            <>
              <span>{t('placeholder.body')}</span>
              <span className="mt-1.5 flex items-center justify-center gap-1.5">
                <span className="label-micro">{t('placeholder.route')}</span>
                {/* The path is data, not copy — it stays LTR (DS-51). */}
                <span className="ltr-num font-mono text-slate-600">{location.pathname}</span>
              </span>
            </>
          }
        />
      </Card>
    </div>
  );
}
